import { useState, useEffect } from 'react';
import { hasAssetsEntitlement } from '@jira/servicedesk__insight-common-services-workspace-context/src/fetchWorkspaceId.tsx';
import { useAssetsLogoAndFaviconResource } from '@jira/router-resources__assets-admin-settings/src/useAssetsNavigationSettingsResource.tsx';
import {
	uploadFavicon as uploadFaviconService,
	resetFaviconToDefault as resetFaviconToDefaultService,
} from '@jira/router-resources__assets-admin-settings/src/updateNavigationSettings.tsx';
import { emptyLogoResponse } from '@jira/router-resources__assets-admin-settings/src/emptyLogoResponse.constants.tsx';
import { useAssetsTenantContext } from '@jira/assets-app__assets-tenant-context-provider/src/useAssetsTenantContext.tsx';
import type { AssetsFaviconResponse } from '@jira/router-resources__assets-admin-settings/src/BannerSettings.types.tsx';

export const useFaviconService = (): {
	faviconState: {
		loading: boolean;
		error: Error | null;
		data: AssetsFaviconResponse | null;
	};
	uploadFavicon: (
		workspaceId: string,
		payload: { file?: File; fileName?: string; imageUrl?: string },
	) => Promise<void>;
	resetFaviconToDefault: (workspaceId: string) => Promise<void>;
	isDefaultFavicon: boolean;
	hasAssetsEntitlementEnabled: boolean;
} => {
	const { data: resourceData, loading, error, update } = useAssetsLogoAndFaviconResource();
	const assetsTenantContext = useAssetsTenantContext();

	const hasAssetsEntitlementEnabled = hasAssetsEntitlement(assetsTenantContext);

	const [faviconState, setFaviconState] = useState({
		loading,
		error: error instanceof Error ? error : null,
		data: resourceData?.favicon ?? null,
	});

	useEffect(() => {
		if (!loading && resourceData) {
			setFaviconState((prev) => ({ ...prev, data: resourceData.favicon }));
		}
	}, [loading, resourceData]);

	const uploadFavicon = async (
		workspaceId: string,
		payload: { file?: File; fileName?: string; imageUrl?: string },
	): Promise<void> => {
		setFaviconState((prev) => ({ ...prev, loading: true, error: null }));

		try {
			const uploadedFavicon = await uploadFaviconService(workspaceId, payload);

			update((currentData) => {
				return currentData
					? { ...currentData, favicon: uploadedFavicon }
					: { logo: emptyLogoResponse, favicon: uploadedFavicon };
			});

			setFaviconState({
				loading: false,
				error: null,
				data: uploadedFavicon,
			});
		} catch (uploadError) {
			setFaviconState({
				loading: false,
				error: uploadError instanceof Error ? uploadError : null,
				data: null,
			});
			throw uploadError;
		}
	};

	const resetFaviconToDefault = async (workspaceId: string): Promise<void> => {
		setFaviconState((prev) => ({ ...prev, loading: true, error: null }));

		try {
			const defaultFavicon = await resetFaviconToDefaultService(workspaceId);

			update((currentData) => {
				return currentData
					? { ...currentData, favicon: defaultFavicon }
					: { logo: emptyLogoResponse, favicon: defaultFavicon };
			});

			setFaviconState({
				loading: false,
				error: null,
				data: defaultFavicon,
			});
		} catch (resetError) {
			setFaviconState({
				loading: false,
				error: resetError instanceof Error ? resetError : null,
				data: null,
			});
			throw resetError;
		}
	};

	return {
		faviconState,
		uploadFavicon,
		resetFaviconToDefault,
		isDefaultFavicon: Boolean(resourceData?.favicon?.isDefaultFavicon),
		hasAssetsEntitlementEnabled,
	};
};
